import _ from 'lodash'
import { EntityState } from '@reduxjs/toolkit'
import { CommonState, Model } from '@/core/classes/Model'
import { SearchRequestOptions } from '@/core/types/request'
import { DictaphoneDocument, Document } from './document.model'
import { Patient } from './patient.model'
import { Staff } from './staff.model'
import { Act, Consultation } from './consultation.model'
import { Network } from './network.model'

export enum ExpertiseService {
  MedConnect = 'medconnect',
  Omnidoc = 'omnidoc',
}

export enum ExpertiseStatus {
  Draft = 'draft',
  Sent = 'sent',
  Reviewed = 'reviewed',
  Closed = 'closed',
  Omnidoc = 'omnidoc',
}

export type Expertise = {
  _id: string
  patient: Patient
  createdBy: Staff
  medecin?: Staff
  network?: Network
  consultations: Consultation[]
  comment?: string
  conclusion?: string
  chatRoom?: string
  diagnostic?: string
  dictaphones?: DictaphoneDocument[]
  files: Document[]
  startDate: string
  endDate?: string
  status: ExpertiseStatus
  speciality: string
  externalServices: {
    [ExpertiseService.Omnidoc]?: {
      id: string
      redirectUrl: string
    }
  }
}

export type CreateExpertiseDto = {
  medecin?: string
  patient?: string
  consultations?: string[]
}

export type UpdateExpertiseDto = Partial<Omit<CreateExpertiseDto, 'conclusion' | 'diagnostic'>> & {
  comment?: string
  speciality?: string
  status?: ExpertiseStatus
}

export type ExpertiseState = CommonState & {
  expertises: EntityState<Expertise, Expertise['_id']>
}

class ExpertiseModel extends Model {
  static instance: ExpertiseModel

  static getInstance() {
    if (!ExpertiseModel.instance) {
      ExpertiseModel.instance = new ExpertiseModel('expertise')
    }
    return ExpertiseModel.instance
  }

  getInitialState(): ExpertiseState {
    return _.merge({}, this.commonState, {
      expertises: {
        entities: {},
        ids: [],
      },
    })
  }

  createExpertise = async (createExpertiseDto: CreateExpertiseDto): Promise<Expertise> => {
    return await this.httpClient.request<Expertise>('/expertises', 'POST', createExpertiseDto)
  }

  createExternalServiceExpertise = async (medconnectExpertiseId: string, externalService: Exclude<ExpertiseService, ExpertiseService.MedConnect>) => {
    return await this.httpClient.request<Expertise>(`/expertises/${medconnectExpertiseId}/${externalService}`, 'POST')
  }

  getExpertise = async (id: string): Promise<Expertise> => {
    return await this.httpClient.request<Expertise>(`/expertises/${id}`, 'GET')
  }

  // TODO: add type for query
  getExpertises = async (query: any, options?: SearchRequestOptions): Promise<[Expertise[], number]> => {
    const defaultOptions = {
      limit: 5,
      order: 'ASC',
      skip: 0,
      orderBy: '',
    }
    return await this.httpClient.request<[Expertise[], number]>('/expertises/query', 'POST', { query, options: _.merge({}, defaultOptions, options) })
  }

  updateExpertise = async (id: string, changes: UpdateExpertiseDto): Promise<Expertise> => {
    return await this.httpClient.request<Expertise>(`/expertises/${id}`, 'PATCH', changes)
  }

  deleteExpertise = async (id: string) => {
    await this.httpClient.request(`/expertises/${id}`, 'DELETE')
  }

  getFSP = async (id: string, acts: Act[]): Promise<Expertise> => {
    return await this.httpClient.request<Expertise>(`/expertises/${id}/fsp`, 'POST', { acts })
  }
}

export default ExpertiseModel.getInstance()
